/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { PageHeader, EmptyState, PrimaryButton, StatusBadge } from "@/components/Shared";
import { dateInputToISO, isoToDateInput } from "@/lib/dateFormat";

/**
 * Generic CRUD page.
 * Props:
 *  - resource: API path segment (e.g. "companies")
 *  - title, subtitle, singular ("Firma")
 *  - fields: array of { name, label, type: text|email|tel|textarea|select|date|datetime|number, options?, required?, placeholder?, optionsFromResource? }
 *  - columns: array of { key, label, render?(row), badge?: "status"|"priority" }
 *  - defaultValues?: object
 *  - testidPrefix: kebab-case used for data-testids
 */
export default function ResourcePage({
  resource,
  title,
  subtitle,
  singular,
  newButtonLabel,
  createDialogTitle,
  editDialogTitle,
  submitButtonLabel,
  emptyDescription,
  emptyCreateButtonLabel,
  fields,
  columns,
  defaultValues = {},
  testidPrefix,
  onRowClick,
  extraParams,
  extraToolbar,
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState(defaultValues);
  const formRef = useRef(defaultValues);
  // Local sticky store for rows created inline in the dialog. These NEVER get
  // cleared by SWR refetches, so freshly created Select options remain available
  // for Radix Select (preventing an accidental onValueChange('') reset when the
  // background refetch has not yet populated the query cache).
  const [inlineCreated, setInlineCreated] = useState({});
  // Ref used to suppress Radix Select's automatic onValueChange('') fired right
  // after we programmatically set a fresh value (SelectItem is not registered in
  // Radix's collection until the dropdown is opened). Maps field.name → timestamp.
  const suppressClearRef = useRef({});
  // Keep ref in sync so we can read the truthful state synchronously,
  // even if a re-render lags behind (e.g. inline-create → parent re-render → race with submit).
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const { data: items = [], isLoading } = useQuery({
    queryKey: [resource, search, extraParams],
    queryFn: async () => {
      const params = { ...(extraParams || {}) };
      if (search) params.q = search;
      const { data } = await api.get(`/${resource}`, { params });
      return data;
    },
  });

  // Preload option sources
  const optionResources = useMemo(
    () => Array.from(new Set(fields.filter((f) => f.optionsFromResource).map((f) => f.optionsFromResource))),
    [fields],
  );
  const optionsQueries = optionResources.map((r) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useQuery({
      queryKey: [r, "all"],
      queryFn: async () => {
        // Dropdowns should still show red-flagged contacts so existing references don't disappear.
        const params = r === "contacts" ? { include_red: "true" } : undefined;
        const { data } = await api.get(`/${r}`, { params });
        return data;
      },
    }),
  );
  const optionsMap = useMemo(() => {
    const m = {};
    optionResources.forEach((r, i) => {
      m[r] = optionsQueries[i]?.data || [];
    });
    return m;
  }, [optionResources, ...optionsQueries.map((q) => q.data)]);

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post(`/${resource}`, payload)).data,
    onSuccess: () => {
      toast.success(`${singular} gespeichert.`);
      qc.invalidateQueries({ queryKey: [resource] });
      setOpen(false);
      setEditing(null);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, payload }) => (await api.put(`/${resource}/${id}`, payload)).data,
    onSuccess: () => {
      toast.success(`${singular} gespeichert.`);
      qc.invalidateQueries({ queryKey: [resource] });
      setOpen(false);
      setEditing(null);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/${resource}/${id}`)).data,
    onSuccess: () => {
      toast.success(`${singular} gelöscht.`);
      qc.invalidateQueries({ queryKey: [resource] });
      setConfirmDelete(null);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  function openCreate() {
    setEditing(null);
    formRef.current = { ...defaultValues };
    setForm({ ...defaultValues });
    setOpen(true);
  }

  function openEdit(row) {
    const next = {};
    fields.forEach((f) => {
      let v = row[f.name];
      if (f.type === "datetime" && v) {
        // datetime-local format
        v = new Date(v).toISOString().slice(0, 16);
      } else if (f.type === "date" && v) {
        v = isoToDateInput(v);
      }
      next[f.name] = v ?? "";
    });
    setEditing(row);
    formRef.current = next;
    setForm(next);
    setOpen(true);
  }

  function handleSubmit(e) {
    e.preventDefault();
    // Build payload – prefer ref (guaranteed most-recent value) with fallback to state.
    const source = { ...(form || {}), ...(formRef.current || {}) };
    const payload = {};
    fields.forEach((f) => {
      let v = source[f.name];
      if (v === "" || v === undefined || v === "__none__") {
        v = null;
      } else if (f.type === "datetime" && v) {
        v = new Date(v).toISOString();
      } else if (f.type === "date" && v) {
        v = dateInputToISO(v);
      } else if (f.type === "number" && v != null) {
        v = v === "" ? null : Number(v);
      }
      if (f.required && (v === null || v === "")) {
        // skip; backend will validate
      }
      payload[f.name] = v;
    });
    if (editing) {
      updateMut.mutate({ id: editing.id, payload });
    } else {
      createMut.mutate(payload);
    }
  }

  return (
    <div data-testid={`${testidPrefix}-page`}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        testid={`${testidPrefix}-header`}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            {extraToolbar}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suchen…"
                className="pl-8 w-56"
                data-testid={`${testidPrefix}-search`}
              />
            </div>
            <PrimaryButton onClick={openCreate} data-testid={`${testidPrefix}-new-btn`}>
              <Plus className="w-4 h-4 mr-1" />
              {newButtonLabel || "Neu"}
            </PrimaryButton>
          </div>
        }
      />

      <div className="crm-card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Lädt…</div>
        ) : items.length === 0 ? (
          <EmptyState
            title={`Noch keine ${title}`}
            description={emptyDescription || `Lege deine erste ${singular} an, um zu starten.`}
            action={
              <PrimaryButton onClick={openCreate} data-testid={`${testidPrefix}-empty-new-btn`}>
                <Plus className="w-4 h-4 mr-1" />
                {emptyCreateButtonLabel || `${singular} anlegen`}
              </PrimaryButton>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="crm-table-header">
                {columns.map((c) => (
                  <TableHead key={c.key} className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {c.label}
                  </TableHead>
                ))}
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow
                  key={row.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  data-testid={`${testidPrefix}-row-${row.id}`}
                  onClick={() => (onRowClick ? onRowClick(row) : openEdit(row))}
                >
                  {columns.map((c) => (
                    <TableCell key={c.key} className="py-3 text-sm text-gray-700">
                      {c.render
                        ? c.render(row)
                        : c.badge
                        ? <StatusBadge value={row[c.key]} />
                        : row[c.key] ?? <span className="text-gray-400">—</span>}
                    </TableCell>
                  ))}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
                          data-testid={`${testidPrefix}-row-menu-${row.id}`}
                          aria-label="Aktionen"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(row)} data-testid={`${testidPrefix}-edit-${row.id}`}>
                          <Pencil className="w-4 h-4 mr-2" /> Bearbeiten
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-rose-600 focus:text-rose-700"
                          onClick={() => setConfirmDelete(row)}
                          data-testid={`${testidPrefix}-delete-${row.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testidPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? (editDialogTitle || `${singular} bearbeiten`) : (createDialogTitle || `Neue ${singular}`)}
            </DialogTitle>
            <DialogDescription>Felder ausfüllen und speichern.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.map((f) => {
                const val = form[f.name] ?? "";
                const fullSpan = f.fullWidth || f.type === "textarea" || Boolean(f.withInlineCreate);
                const colClass = fullSpan
                  ? "md:col-span-2"
                  : f.colSpan === "half"
                  ? "md:col-span-1"
                  : "";
                const setField = (name, value) =>
                  setForm((s) => ({ ...s, [name]: value }));
                const baseOptions = f.optionsFromResource
                  ? (() => {
                      const cached = optionsMap[f.optionsFromResource] || [];
                      const extras = inlineCreated[f.optionsFromResource] || [];
                      // Merge sticky inline-created rows first so they are always available
                      // for Radix Select, even before the background refetch completes.
                      const merged = [
                        ...extras.filter((e) => !cached.some((c) => c.id === e.id)),
                        ...cached,
                      ];
                      return merged.filter((it) =>
                        f.optionsFilter ? f.optionsFilter(it, { form, editing }) : true,
                      );
                    })()
                  : null;
                return (
                  <div key={f.name} className={colClass}>
                    <Label htmlFor={`f-${f.name}`} className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                      {f.label}
                      {f.required ? <span className="text-rose-500"> *</span> : null}
                    </Label>
                    {f.type === "textarea" ? (
                      <Textarea
                        id={`f-${f.name}`}
                        value={val || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setField(f.name, v);
                          if (f.onValueChange) f.onValueChange(v, setField, form);
                        }}
                        rows={f.rows || 4}
                        placeholder={f.placeholder}
                        className="mt-1"
                        data-testid={`${testidPrefix}-field-${f.name}`}
                      />
                    ) : f.type === "select" ? (
                      <div
                        className={
                          f.withInlineCreate
                            ? "mt-1 flex flex-col sm:flex-row sm:items-stretch gap-2"
                            : "flex items-center gap-2 mt-1"
                        }
                        data-testid={`${testidPrefix}-field-${f.name}-wrap`}
                        data-current-value={String(val || "")}
                      >
                        <div className="flex-1 min-w-0">
                          <Select
                            value={val ? String(val) : ""}
                            onValueChange={(v) => {
                              // Guard: Radix Select fires onValueChange('') when a value is
                              // set programmatically to an id whose SelectItem is not yet
                              // registered in Radix's internal collection (dropdown closed).
                              // This happens right after an inline-create. Ignore the clear
                              // within a short window to preserve our known-good state.
                              if (v === "" && suppressClearRef.current[f.name] && Date.now() - suppressClearRef.current[f.name] < 800) {
                                return;
                              }
                              setField(f.name, v);
                              if (f.onValueChange) f.onValueChange(v, setField, form);
                            }}
                          >
                            <SelectTrigger data-testid={`${testidPrefix}-field-${f.name}`}>
                              <SelectValue placeholder={f.placeholder || "Auswählen"} />
                            </SelectTrigger>
                            <SelectContent>
                              {f.allowNone ? (
                                <SelectItem value="__none__">{f.allowNone.label || "— keine Zuordnung —"}</SelectItem>
                              ) : null}
                              {(baseOptions
                                ? baseOptions.map((it) => ({
                                    value: it.id,
                                    label: f.optionLabel ? f.optionLabel(it) : it.name || `${it.first_name} ${it.last_name}`,
                                  }))
                                : f.options || []
                              ).map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {f.withInlineCreate ? (
                          <InlineCreateButton
                            spec={f.withInlineCreate}
                            onCreated={(newRow) => {
                              const res = f.withInlineCreate.resource;
                              // 0) Suppress Radix auto-clear for the next few hundred ms.
                              suppressClearRef.current[f.name] = Date.now();
                              // 1) Sticky-Store synchron pflegen – garantiert, dass die
                              //    passende SelectItem gerendert wird BEVOR der Wert gesetzt wird.
                              //    Verhindert Radix-Select-Auto-Reset (onValueChange('')).
                              setInlineCreated((prev) => ({
                                ...prev,
                                [res]: [newRow, ...((prev[res] || []).filter((r) => r.id !== newRow.id))],
                              }));
                              // 2) TanStack-Query-Cache preemptiv aktualisieren.
                              qc.setQueryData(
                                [res, "all"],
                                (prev) => [newRow, ...(Array.isArray(prev) ? prev.filter((r) => r.id !== newRow.id) : [])],
                              );
                              // 3) Ref SYNCHRON updaten – authoritative source für handleSubmit.
                              formRef.current = { ...(formRef.current || {}), [f.name]: newRow.id };
                              // 4) State updaten für UI-Anzeige (Select-Trigger).
                              setForm((s) => ({ ...(s || {}), [f.name]: newRow.id }));
                              // 5) Server-Truth nachziehen.
                              qc.invalidateQueries({ queryKey: [res, "all"] });
                            }}
                            testid={`${testidPrefix}-field-${f.name}-inline-new`}
                          />
                        ) : null}
                      </div>
                    ) : (
                      <Input
                        id={`f-${f.name}`}
                        type={f.type === "datetime" ? "datetime-local" : f.type === "date" ? "date" : f.type || "text"}
                        value={val || ""}
                        required={f.required}
                        step={f.step}
                        placeholder={f.placeholder}
                        onChange={(e) => {
                          const v = e.target.value;
                          setField(f.name, v);
                          if (f.onValueChange) f.onValueChange(v, setField, form);
                        }}
                        onBlur={(e) => {
                          if (f.onBlur) f.onBlur(e.target.value, setField, form);
                        }}
                        className="mt-1"
                        data-testid={`${testidPrefix}-field-${f.name}`}
                      />
                    )}
                    {f.helperText ? (
                      <p className="text-xs text-gray-500 mt-1">{f.helperText}</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid={`${testidPrefix}-cancel-btn`}>
                Abbrechen
              </Button>
              <Button
                type="submit"
                className="bg-black text-white hover:bg-gray-800"
                disabled={createMut.isPending || updateMut.isPending}
                data-testid={`${testidPrefix}-submit-btn`}
              >
                {submitButtonLabel || (editing ? "Speichern" : "Anlegen")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid={`${testidPrefix}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{singular} wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dieser Vorgang kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testidPrefix}-delete-cancel`}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => deleteMut.mutate(confirmDelete.id)}
              data-testid={`${testidPrefix}-delete-confirm`}
            >
              {singular} löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * InlineCreateButton
 * Prop `spec`: { resource, singular, fields: [...], initialValues?, buttonLabel? }
 * Öffnet einen sekundären Dialog, um schnell einen Sub-Datensatz anzulegen.
 * Der übergeordnete Dialog bleibt geöffnet.
 */
function InlineCreateButton({ spec, onCreated, testid }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(spec.initialValues || {});
  const [saving, setSaving] = useState(false);

  function reset() {
    setForm(spec.initialValues || {});
  }

  async function handleSave() {
    const payload = {};
    (spec.fields || []).forEach((f) => {
      let v = form[f.name];
      if (v === "" || v === undefined || v === "__none__") v = null;
      else if (f.type === "date" && v) v = dateInputToISO(v);
      payload[f.name] = v;
    });
    const requiredMissing = (spec.fields || []).find((f) => f.required && (payload[f.name] == null || payload[f.name] === ""));
    if (requiredMissing) {
      toast.error(`Bitte gib „${requiredMissing.label}“ noch an.`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/${spec.resource}`, payload);
      toast.success(`${spec.singular} gespeichert.`);
      onCreated?.(data);
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => { reset(); setOpen(true); }}
        data-testid={testid}
        className="whitespace-nowrap shrink-0 w-full sm:w-auto"
      >
        <Plus className="w-3.5 h-3.5 mr-1" />
        {spec.buttonLabel || `${spec.singular} anlegen`}
      </Button>
      <Dialog open={open} onOpenChange={(v) => !saving && setOpen(v)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle>{spec.singular} anlegen</DialogTitle>
            <DialogDescription>Nach dem Speichern wird die {spec.singular.toLowerCase()} direkt zugeordnet.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(spec.fields || []).map((f) => {
              const val = form[f.name] ?? "";
              return (
                <div key={f.name}>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    {f.label}{f.required ? <span className="text-rose-500"> *</span> : null}
                  </Label>
                  {f.type === "textarea" ? (
                    <Textarea
                      value={val}
                      rows={f.rows || 3}
                      placeholder={f.placeholder}
                      onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
                      data-testid={`${testid}-field-${f.name}`}
                    />
                  ) : f.type === "select" ? (
                    <Select
                      value={val ? String(val) : ""}
                      onValueChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))}
                    >
                      <SelectTrigger data-testid={`${testid}-field-${f.name}`}>
                        <SelectValue placeholder={f.placeholder || "Auswählen"} />
                      </SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={f.type === "date" ? "date" : f.type || "text"}
                      value={val}
                      placeholder={f.placeholder}
                      onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
                      onBlur={(e) => f.onBlur?.(e.target.value, (name, v) => setForm((p) => ({ ...p, [name]: v })), form)}
                      data-testid={`${testid}-field-${f.name}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setOpen(false)} disabled={saving} data-testid={`${testid}-cancel`}>
              Abbrechen
            </Button>
            <Button
              type="button"
              className="bg-black text-white hover:bg-gray-800"
              onClick={handleSave}
              disabled={saving}
              data-testid={`${testid}-save`}
            >
              {saving ? "Speichern…" : `${spec.singular} speichern`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

